import React from "react";
import PropTypes from "prop-types";
import dynamic from "next/dynamic";
import TextInfo from "@/bit/components/components.text";
import getColor from "@gcloud-npm/utils.enums.colors";
import Card from "@/bit/components/components.cms-block.cms-small-feature-card";
import Carousel from "@/bit/components/components.carousel";
import emptyCard from "./emptyCard.json";

const IconGlyph = dynamic(() => import("@/bit/components/components.icon-glyph"));
const CmsAddElement = dynamic(
  () => import("@/bit/components/components.cms-block.cms-add-element")
);
const ID_TEMPLATE_ORION = "1405741";

/**
 * Componente que permite crear un carrousel de SmallFeatureCards
 */
function SmallFeatureCardList(props) {
  const { cmsHandler, blockName, cms, alias, loaded = true } = props;

  const carrusel = props.carrusel;
  const color = props.color || "white";
  const backgroundColor = getColor(color);
  const titleProps = props["cms-atom-text-1"];
  const cardList = props["cms-small-feature-card-1"] || props["cms-ml-detail-card-1"];

  const className = "cms-small-feature-card-list";

  if (
    !cardList ||
    !Array.isArray(cardList) ||
    (cardList.length === 0 && !cms && !process.env.NEXT_PUBLIC_ASTRO_MAKER)
  ) {
    return null;
  }

  // Icono Añadir elemento
  const addElementCmp = (index = "start") =>
    cmsHandler && (
      <CmsAddElement
        addProps={{
          index,
          idTemplateOrion: ID_TEMPLATE_ORION,
          itemIdDotNotation: `${blockName}.cms-small-feature-card-1`,
          alias,
        }}
        cmsHandler={cmsHandler}
      />
    );

  return (
    <section
      id={blockName || ""}
      className={className}
      style={{ backgroundColor: backgroundColor }}
    >
      {cmsHandler && (
        <span
          className={"element-editable block edit-component"}
          onClick={() =>
            cmsHandler({
              ...props,
              name: "cms-block.cms-detail-card-list",
            })
          }
        >
          <IconGlyph id="lapiz-m" height={28} color="white" />
        </span>
      )}
      <div className="container">
        <div className="title-gap">
          <TextInfo {...titleProps} cmsHandler={cmsHandler} />
        </div>
        <div className="content-gap">
          {/* Card placeholder cuando la lista es vacia */}
          {(cms || process.env.NEXT_PUBLIC_ASTRO_MAKER) && cardList.length === 0 ? (
            <>
              <Card cardProps={emptyCard} />
              {addElementCmp("end")}
            </>
          ) : (
            <>
              {carrusel === true || carrusel === "true" ? (
                <Carousel maxPointNum={0} breakpoint={768}>
                  {[
                    addElementCmp("start"),
                    ...cardList.map((item, index) => (
                      <Card
                        cardProps={{...item, loaded}}
                        cmsHandler={cmsHandler}
                        index={index}
                        alias={alias}
                        blockName={blockName}
                        key={`card-${index}`}
                        frontalListToDelete={cardList}
                      />
                    )),
                    addElementCmp("end"),
                  ]}
                </Carousel>
              ) : (
                <div className={`${className}--grid`}>
                  {[
                    addElementCmp("start"),
                    cardList.map((item, index) => (
                      <Card
                        cardProps={{...item, loaded}}
                        index={index}
                        alias={alias}
                        blockName={blockName}
                        cmsHandler={cmsHandler}
                        key={`card-${index}`}
                        frontalListToDelete={cardList}
                      />
                    )),
                    addElementCmp("end"),
                  ]}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <style jsx>{`.cms-small-feature-card-list {
  background-color: white;
  padding: 1rem 0;
  position: relative;

  hr.separator {
    color: rgb(221, 221, 221);
    margin-top: 0.5rem;
    margin-bottom: 2rem;
  }
  .title-gap {
    margin-bottom: 1rem;
  }
  :global(.small-feature-card .card-content) {
    width: 189px;
    min-width: 189px;
  }
  :global(.carousel-window) {
    gap: 1.5rem;
  }

  &--grid {
    position: relative;
    display: grid;
    gap: 24px;
    grid-template-columns: repeat(auto-fill, minmax(310px, 1fr));
    &::-webkit-scrollbar {
      display: none;
    }
  }
  @media screen and (max-width: 768px) {
    :global(.carousel) {
      :global(.carousel-window) {
        gap: 0.25rem;
      }
    }
    &--grid {
      display: grid;
      gap: 8px;
      overflow-x: auto;
      overflow-y: hidden;
      height: 100%;
      grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
      &::-webkit-scrollbar {
        display: none;
      }
      :global(.Card) {
        height: auto !important;
      }
    }
  }
}`}</style>    
    </section>
  );
}

SmallFeatureCardList.propTypes = {
  /**
   * {
   *  "info": "Permite heredar cards implementadas en otras fichas, hay que indicarlo empleando la notacion: [alias]::[objectDotPath]::[start|end]",
   *  "kind": "both",
   *  "beautifulName": "Heredar de:"
   * }
   */
  cmsgetfrom: PropTypes.string,
  /**
   * {
   *  "info": "Define si las cards se muestran en carrusel o en grid",
   *  "kind": "both",
   *  "beautifulName": "Carrusel"
   * }
   */
  carrusel: PropTypes.string,
  /**
   * {
   *  "info": "Color de fondo",
   *  "kind": "both",
   *  "beautifulName": "Color de fondo"
   * }
   */
  color: PropTypes.string,
};

SmallFeatureCardList.defaultProps = {
  carrusel: "true",
  color: "white",
};

export default SmallFeatureCardList;
